import { StorageAdapter } from './storageInterface';

/**
 * Adapter pour Median Native Datastore (App Storage)
 * Stockage natif iOS/Android persistant via UserDefaults/SharedPreferences
 */
export class MedianStorageAdapter implements StorageAdapter {
  private get median() {
    return (window as any).median?.storage?.app;
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const result = await this.median.get({ key });
      
      // Gestion des status d'erreur Median selon la documentation
      if (result?.status === 'read-error') {
        console.error(`[MedianStorage] Read error for ${key}`);
        return null;
      }
      if (result?.status === 'preference-not-found') {
        return null;
      }
      
      if (result?.data) {
        return JSON.parse(result.data) as T;
      }
      return null;
    } catch (error) {
      console.error(`[MedianStorage] Error getting ${key}:`, error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      await this.median.set({
        key,
        value: JSON.stringify(value)
      });
    } catch (error) {
      console.error(`[MedianStorage] Error setting ${key}:`, error);
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.median.delete({ key });
    } catch (error) {
      console.error(`[MedianStorage] Error removing ${key}:`, error);
    }
  }

  async clear(): Promise<void> {
    // ⚠️ Ne PAS supprimer authSession et dismissedVersion lors d'un clear normal
    // Ces données doivent persister même après un cache clear
    const clearableKeys = [
      'koken.offlineQueue',
      'koken.pendingValidations',
      'koken.challengeCache',
      'koken.sessionData'
    ];
    
    await Promise.all(clearableKeys.map(key => this.remove(key)));
  }

  async keys(): Promise<string[]> {
    // Median ne supporte pas l'énumération des clés
    return [
      'koken.offlineQueue',
      'koken.pendingValidations',
      'koken.userPreferences',
      'koken.challengeCache',
      'koken.sessionData',
      'koken.authSession',
      'koken.dismissedVersion'
    ];
  }
}
